import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import useApi from '../api';
import toastConfig from '../config/Toast';
import { useParams, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Check, X } from 'lucide-react';

export default function QuestionView({ questionId, onClose }: { questionId?: string, onClose?: () => void }) {
    const [question, setQuestion] = useState<string>('');
    const [answer, setAnswer] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [extractionReason, setExtractionReason] = useState<string | null>(null);
    const [extractionConfidence, setExtractionConfidence] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [conversation, setConversation] = useState<any>();
    const [messages, setMessages] = useState<any[]>([]);
    const params = useParams();
    const id = params.id || questionId;
    const { get, put, post } = useApi();

    useEffect(() => {
        if (id) {
            fetchQuestion();
        }
    }, [id]);

    const fetchQuestion = async () => {
        if (!id) return;
        
        try {
            setLoading(true);
            const response = await get(`/questions/${id}`);
            setQuestion(response.data.question);
            setAnswer(response.data.answer || '');
            setStatus(response.data.status);
            setExtractionReason(response.data.extraction_reason || null);
            setExtractionConfidence(response.data.extraction_confidence ?? null);
            setConversation(response.data.first_conversation);
            setMessages(response.data.messages);
        } catch (error) {
            console.error('Error fetching question:', error);
            toast.error('Failed to fetch question.', toastConfig);
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async () => {
        if (!id) return;
        if (question.trim() === "") {
            toast.error('Question is required.', toastConfig);
            return;
        }
        if (answer.trim() === "") {
            toast.error('Answer is required.', toastConfig);
            return;
        }

        try {
            setActionLoading(true);
            await post(`/questions/${id}/accept`, { question, answer });
            toast.success('Question accepted and added to knowledge base.', toastConfig);
            if (onClose) onClose();
        } catch (error) {
            console.error('Error accepting question:', error);
            toast.error('Failed to accept question.', toastConfig);
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!id) return;
        
        try {
            setActionLoading(true);
            await post(`/questions/${id}/reject`, {});
            toast.success('Question rejected and deleted.', toastConfig);
            if (onClose) onClose();
        } catch (error) {
            console.error('Error rejecting question:', error);
            toast.error('Failed to reject question.', toastConfig);
        } finally {
            setActionLoading(false);
        }
    };

    const updateQuestion = async () => {
        if (question === "") {
            toast.error('Question is required.', toastConfig);
            return;
        }
        if (answer === "") {
            toast.error('Answer is required.', toastConfig);
            return;
        }

        const data = {
            question: question,
            answer: answer,
        }

        try {
            setLoading(true);
            await put(`/questions/${id}`, data);
            toast.success('Question successfully updated.', toastConfig);
            if (onClose) onClose();
        } catch (error) {
            console.error('Error updating question:', error);
            toast.error('Failed to update question.', toastConfig);
        } finally {
            setLoading(false);
        }
    };

    const isForReview = status === 'for_review';
    
    const getStatusBadge = () => {
        if (status === 'answered') return <Badge variant="outline">ANSWERED</Badge>;
        if (status === 'for_review') return <Badge variant="secondary">FOR REVIEW</Badge>;
        return <Badge variant="destructive">UNANSWERED</Badge>;
    };

    return (
        <div className="mt-4 flex flex-col gap-8 p-8 w-full mx-auto">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Question</h2>
                {status && getStatusBadge()}
            </div>
            
            {isForReview && (
                <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <p className="text-amber-800 dark:text-amber-200 text-sm">
                        This question was extracted from a human agent conversation. Review and edit the question and
                        answer below, then accept to add it to your knowledge base or reject to discard.
                    </p>
                </div>
            )}

            {(extractionReason || extractionConfidence !== null) && (
                <div className="border rounded-lg p-4 bg-muted/50">
                    <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold">Why this was extracted</p>
                        {extractionConfidence !== null && (
                            <Badge variant="outline">{Math.round(extractionConfidence * 100)}% confidence</Badge>
                        )}
                    </div>
                    {extractionReason && (
                        <p className="text-sm text-muted-foreground">{extractionReason}</p>
                    )}
                </div>
            )}

            <div>
                <p className="text-muted-foreground mb-4">
                    {isForReview ? 'Review and edit the extracted question.' : 'Update your question here.'}
                </p>
                <Textarea
                    id="question"
                    placeholder="Enter your question..."
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                />
            </div>

            <div>
                <h2 className="text-2xl font-bold">Answer</h2>
                <p className="text-muted-foreground mb-4">
                    {isForReview ? 'Review and edit the extracted answer.' : 'Update the answer to your question.'}
                </p>
                <Textarea
                    id="answer"
                    placeholder="Enter your answer..."
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                />
            </div>

            {conversation && (
                <div>
                    <h2 className="text-2xl font-bold mb-4">Related Conversation</h2>
                    <Link to={`/sessions/${conversation.id}`}>
                        <div className="border rounded-lg p-4 hover:bg-accent max-h-48 overflow-y-auto">
                            {messages
                                .filter(msg => msg.conversation_id === conversation.id)
                                .map((msg, index) => (
                                    <div key={index} className="mb-2 last:mb-0">
                                        {msg.content}
                                    </div>
                                ))
                            }
                        </div>
                    </Link>
                </div>
            )}
            
            {isForReview ? (
                <div className="flex gap-4">
                    <Button
                        onClick={handleAccept}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        disabled={actionLoading}
                    >
                        <Check className="h-4 w-4 mr-2" />
                        {actionLoading ? 'Processing...' : 'Accept & Add to Knowledge Base'}
                    </Button>
                    <Button
                        onClick={handleReject}
                        variant="destructive"
                        className="flex-1"
                        disabled={actionLoading}
                    >
                        <X className="h-4 w-4 mr-2" />
                        {actionLoading ? 'Processing...' : 'Reject & Delete'}
                    </Button>
                </div>
            ) : (
                <Button
                    onClick={updateQuestion}
                    className="w-full"
                    disabled={loading}
                >
                    {loading ? 'Updating...' : 'Update Question'}
                </Button>
            )}
        </div>
    );
}